import { useEffect } from "react";
import { useSelector } from "react-redux";
import ICONS from "../../../../../assets/Icons";
import globalText from "../../../../../utils/globalText";
import DataActionField from "../../../../_utils/TransactionCommonComponents/DataActionField";

const BeneficiaryRecreateTxnStepAdditionalDetails = ({
  txnPayLoad,
  onCopyLabels,
  initialRefactorAdditionalDetail,
  setInitialRefactorAdditionalDetail,
  setSelectedValue,
  setSelectedValueMonth,
  setStartDateCal,
  setStartDate,
  setEndDate,
  setTimeData,
  additionalData,
  setAdditionalData,
  setQuantities,
}) => {
  const { beneTxnAccountData } = useSelector(
    (state) => state?.beneTxnViewAccData,
  );

  const prevLabels = beneTxnAccountData?.transaction?.[0]?.labels || [];
  const labelsCopied =
    initialRefactorAdditionalDetail?.["__labels__"]?.isCopyDirect;
  const displayLabels = prevLabels.length ? prevLabels.join(", ") : "No Labels";

  useEffect(() => {
    if (!initialRefactorAdditionalDetail?.["__labels__"]?.isCopyDirect) return;

    const prevLabels = [
      ...(beneTxnAccountData?.transaction?.[0]?.labels || []),
    ].sort();

    const currentLabels = [
      ...(txnPayLoad?.labels || []),
      ...(txnPayLoad?.ownerLabels || []),
    ].sort();

    const stillMatches =
      prevLabels.length === currentLabels.length &&
      prevLabels.every((l, i) => l === currentLabels[i]);

    if (!stillMatches) {
      setInitialRefactorAdditionalDetail((prev) => ({
        ...prev,
        ["__labels__"]: {
          ...prev["__labels__"],
          isCopyDirect: false,
        },
      }));
    }
  }, [txnPayLoad?.labels, txnPayLoad?.ownerLabels]);

  useEffect(() => {
    const originalFields =
      beneTxnAccountData?.transaction?.[0]?.additional_data?.data ?? [];

    const originalMap = {};
    originalFields.forEach((field) => {
      originalMap[field.name] = field.value;
    });

    setInitialRefactorAdditionalDetail((prev) => {
      let updated = { ...prev };
      let changed = false;

      Object.keys(additionalData).forEach((key) => {
        const original =
          prev[key]?.data !== undefined ? prev[key]?.data : originalMap[key];

        const current = additionalData[key];
        let isSameAsOriginal = false;

        const isEmpty =
          !current ||
          (typeof current === "object" &&
            Object.values(current).every((v) => !v)) ||
          (Array.isArray(current) && current.length === 0) ||
          current === "";

        if (isEmpty) {
          isSameAsOriginal = false;
        } else if (current?.hasOwnProperty?.("start_date")) {
          isSameAsOriginal =
            String(current?.start_date ?? "") ===
              String(original?.start_date ?? "") &&
            String(current?.end_date ?? "") ===
              String(original?.end_date ?? "");
        } else if (current?.hasOwnProperty?.("to")) {
          isSameAsOriginal =
            String(current?.to ?? "") === String(original?.to ?? "") &&
            String(current?.from ?? "") === String(original?.from ?? "");
        } else if (Array.isArray(current)) {
          const sort1 = [...current].map(String).sort();
          const sort2 = [...(Array.isArray(original) ? original : [])]
            .map(String)
            .sort();
          isSameAsOriginal =
            sort1.length === sort2.length &&
            sort1.every((v, i) => v === sort2[i]);
        } else {
          isSameAsOriginal =
            original !== null &&
            original !== undefined &&
            String(current ?? "") === String(original ?? "");
        }

        // isCopyClick resets the moment user edits away from pasted value
        const pastedValue = prev[key]?.isPaste?.value;
        const isCopyClick =
          prev[key]?.isCopyClick === true &&
          pastedValue !== undefined &&
          String(current) === String(pastedValue)
            ? true
            : false;

        // isCopyDirect derived ONLY from comparison — never overridden by isCopyClick
        const isCopyDirect = isSameAsOriginal;

        if (
          prev[key]?.isCopyDirect !== isCopyDirect ||
          prev[key]?.isCopyClick !== isCopyClick ||
          prev[key]?.data === undefined
        ) {
          updated[key] = {
            ...prev[key],
            data: original,
            isCopyDirect,
            isCopyClick,
          };
          changed = true;
        }
      });

      return changed ? updated : prev;
    });
  }, [additionalData]);

  const handleInputChange = (e, data_type) => {
    if (data_type === "numeric" && e.target.value.length > 15) return;

    if (e.preventDefault) e.preventDefault();
    const { name, value, data_typeChan } = e.target || {
      name: e.name,
      value: e.value,
    };

    const field = txnPayLoad?.selectedCategory?.configuration?.data?.find(
      (field) => field.name === name,
    );

    let updatedData = { ...additionalData };

    const trimmedValue =
      data_typeChan === "date" ||
      data_typeChan === "year" ||
      data_typeChan === "toggle" ||
      data_typeChan === "time" ||
      data_typeChan === "quantity" ||
      data_typeChan === "from_year_to_year" ||
      data_typeChan === "month_year" ||
      data_typeChan === "date_range" ||
      data_typeChan === "multi_select_list" ||
      data_typeChan === "upload"
        ? value
        : value?.replace?.(/^\s+/, "") || "";

    if (field?.data_type === "numeric") {
      if (trimmedValue === "" || /^\d+$/.test(trimmedValue)) {
        updatedData[name] = trimmedValue;
        setAdditionalData(updatedData);
      }
    } else {
      updatedData[name] = trimmedValue;
      setAdditionalData(updatedData);
    }
  };

  return (
    <div className="w-full flex flex-col lg:flex-row Qsmd:max-h-half-screen-height overflow-y-auto h-full">
      <div className="w-width-full lg:w-width-md flex flex-col gap-gap-sm pl-padding-md Qsmd:pl-padding-base pt-padding-md Qsmd:pt-padding-base pr-padding-md lg:pr-padding-lg mt-[var(--banklogo-width-lg)] Qsmd:mt-margin-md">
        <div className="text-text-primary text-base font-semibold flex flex-col gap-gap-xl mb-margin-sm">
          Additional Details
        </div>
        <div className="w-full flex flex-col gap-gap-sm">
          {beneTxnAccountData?.transaction?.[0]?.additional_data?.data?.filter(
            (data) => data?.data_type !== "upload",
          )?.length ? (
            beneTxnAccountData?.transaction?.[0]?.additional_data?.data
              ?.filter((data) => data?.data_type !== "upload")
              ?.map((details, index) => {
                const hasData =
                  details?.value !== null &&
                  details?.value !== undefined &&
                  !(
                    typeof details?.value === "string" &&
                    details.value.trim() === ""
                  ) &&
                  !(
                    Array.isArray(details?.value) && details.value.length === 0
                  ) &&
                  !(
                    typeof details?.value === "object" &&
                    !Array.isArray(details.value) &&
                    Object.values(details.value || {}).every(
                      (v) => v === null || v === undefined || v === "",
                    )
                  );

                const isStringType =
                  details?.data_type === "hyperlink" ||
                  details?.data_type === "string" ||
                  details?.data_type === "numeric";

                // Single flag for green — same for ALL types
                const isGreen =
                  initialRefactorAdditionalDetail?.[details?.name]
                    ?.isCopyDirect;

                const icon = !hasData
                  ? ICONS?.AUTOFILL_WHITE
                  : isGreen
                    ? ICONS?.SELECTED_WITH_GREEN_CIRCLE
                    : isStringType
                      ? ICONS?.COPY
                      : ICONS?.AUTOFILL_WHITE;

                return (
                  <DataActionField
                    key={`${index}-additional-data`}
                    subLabel={details?.name}
                    value={
                      !hasData
                        ? "No Data"
                        : typeof details?.value === "boolean"
                          ? JSON.stringify(details?.value)
                          : typeof details?.value === "object"
                            ? details?.data_type === "from_year_to_year"
                              ? `${details?.value?.from} / ${details?.value?.to}`
                              : details?.data_type === "date_range"
                                ? `${details?.value?.start_date} - ${details?.value?.end_date}`
                                : details?.value?.map(
                                    (val, i) =>
                                      `${val}${details?.value?.length - 1 === i ? "" : ", "}`,
                                  )
                            : details?.value
                    }
                    disabled={!hasData}
                    icon={icon}
                    hoverEnabled={hasData && !isGreen}
                    onClick={() => {
                      if (!hasData) return;
                      if (isStringType) {
                        let copy = { ...details };
                        if (details?.data_type === "numeric") {
                          copy.value = String(copy?.value);
                        }
                        setInitialRefactorAdditionalDetail((prev) => ({
                          ...prev,
                          [details?.name]: {
                            ...prev[details?.name],
                            isCopyClick: true,
                            isCopyDirect: true, // mark green immediately on click
                            isPaste:
                              details?.data_type === "numeric" ? copy : details,
                          },
                        }));
                      } else if (details?.data_type === "quantity") {
                        setInitialRefactorAdditionalDetail((prev) => ({
                          ...prev,
                          [details?.name]: {
                            ...prev[details?.name],
                            isCopyDirect: true,
                          },
                        }));

                        const key = `${details.name}_${details.data_type}`;
                        setQuantities((prev) => ({
                          ...prev,
                          [key]: Number(details?.value),
                        }));

                        handleInputChange({
                          target: {
                            name: details?.name,
                            value: details?.value,
                            data_typeChan: details?.data_type,
                          },
                        });
                      } else {
                        setInitialRefactorAdditionalDetail((prev) => ({
                          ...prev,
                          [details?.name]: {
                            ...prev[details?.name],
                            isCopyDirect: true,
                          },
                        }));

                        const key = `${details.name}_${details.data_type}`;
                        setQuantities((prev) => ({
                          ...prev,
                          [key]: Number(details?.value),
                        }));

                        if (details?.data_type === "from_year_to_year") {
                          setSelectedValue((prev) => ({
                            ...prev,
                            [`${details.name}_${details.data_type}`]:
                              details?.value,
                          }));
                        } else if (details?.data_type === "date") {
                          setStartDate((prev) => ({
                            ...prev,
                            [`${details.name}_${details.data_type}`]: new Date(
                              details?.value,
                            ),
                          }));
                        } else if (details?.data_type === "month_year") {
                          const [month, year] = details?.value
                            .split("/")
                            .map(Number);
                          const monthNames = [
                            "January",
                            "February",
                            "March",
                            "April",
                            "May",
                            "June",
                            "July",
                            "August",
                            "September",
                            "October",
                            "November",
                            "December",
                          ];
                          setSelectedValueMonth((prev) => ({
                            ...prev,
                            [`${details.name}_${details.data_type}`]: {
                              month: monthNames[month - 1],
                              year,
                            },
                          }));
                        } else if (details?.data_type === "date_range") {
                          const parse = (d) => {
                            if (!d) return null;
                            // Try "DD/MM/YYYY" format
                            if (d.includes("/")) {
                              const [day, month, year] = d
                                .split("/")
                                .map(Number);
                              return new Date(year, month - 1, day);
                            }
                            // Try "D MMM, YYYY" format e.g. "5 Jan, 2024"
                            const parsed = new Date(d);
                            return isNaN(parsed.getTime()) ? null : parsed;
                          };

                          const startVal = parse(details?.value?.start_date);
                          const endVal = parse(details?.value?.end_date);

                          if (startVal) {
                            setStartDate((prev) => ({
                              ...prev,
                              [`${details.name}_${details.data_type}`]:
                                startVal,
                            }));
                          }

                          if (endVal) {
                            setEndDate((prev) => ({
                              ...prev,
                              [`${details.name}_${details.data_type}`]: endVal,
                            }));
                          }
                        } else if (details?.data_type === "time") {
                          const [time, meridiems] = details?.value
                            .trim()
                            .split(" ");
                          const [hours, minutes] = time.split(":");
                          setTimeData((prev) => ({
                            ...prev,
                            [`${details.name}_${details.data_type}`]: {
                              hours,
                              minutes,
                              meridiems,
                            },
                          }));
                        }

                        handleInputChange({
                          target: {
                            name: details?.name,
                            value: details?.value,
                            data_typeChan: details?.data_type,
                          },
                        });
                      }
                    }}
                  />
                );
              })
          ) : (
            <p className="text-text-secondary text-font-sm">
              {globalText?.ADDITIONAL_DETAILS_UNSUPPORTED}
            </p>
          )}
        </div>
        <div className="invisible hidden lg:flex leading-[var(--icon-xs)]">
          Don't remove this div
        </div>
      </div>

      {/* labels code */}
      <div className="w-width-full lg:w-width-md mt-margin-md pl-padding-md Qsmd:pl-padding-base pr-padding-md mb-margin-md lg:mb-margin-base lg:pl-padding-sm">
        <div className="text-text-primary text-base font-semibold mb-margin-md">
          Labels (Optional)
        </div>
        <div
          style={{
            opacity: prevLabels.length
              ? "var(--base-opacity)"
              : "var(--light-opacity)",
          }}
        >
          <DataActionField
            value={displayLabels}
            icon={
              labelsCopied
                ? ICONS?.SELECTED_WITH_GREEN_CIRCLE
                : ICONS?.AUTOFILL_WHITE
            }
            hoverEnabled={!!prevLabels.length && !labelsCopied}
            disabled={!prevLabels.length}
            onClick={() => {
              if (!prevLabels.length) return;

              setInitialRefactorAdditionalDetail((prev) => ({
                ...prev,
                ["__labels__"]: {
                  isCopyDirect: true,
                  data: prevLabels,
                },
              }));

              onCopyLabels(prevLabels);
            }}
          />
        </div>
        <div className="invisible hidden lg:flex leading-[var(--icon-xs)]">
          Don't remove this div
        </div>
      </div>
    </div>
  );
};

export default BeneficiaryRecreateTxnStepAdditionalDetails;
